import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Link,
  Button,
  Box,
} from "@mui/material";
import {
  getDatabase,
  ref,
  onValue,
  update,
} from "firebase/database";

const AdminClaveDialog = ({ open, onClose, onSuccess }) => {
  const [clave, setClave] = useState("");
  const [error, setError] = useState("");
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [telefonoInput, setTelefonoInput] = useState("");
  const [telefonoError, setTelefonoError] = useState("");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [clavesData, setClavesData] = useState({});
  const [editFields, setEditFields] = useState({});
  const [cambios, setCambios] = useState(false);

  const db = getDatabase();

  const verificarClave = () => {
    const adminRef = ref(db, "nature/Awoo/Control/Seguridad/Admin");
    onValue(
      adminRef,
      (snapshot) => {
        const claveCorrecta = snapshot.val();
        if (clave === claveCorrecta) {
          setError("");
          onSuccess(); // Acción que se ejecuta si la clave es correcta
          onClose();
          obtenerTodasLasClaves(); // Mostrar el nuevo diálogo de edición
        } else {
          setError("❌ Clave incorrecta");
        }
      },
      { onlyOnce: true }
    );
  };

  const enviarRecuperacion = () => {
    const celularRef = ref(db, "nature/Awoo/Control/Seguridad/Celular");
    const claveRef = ref(db, "nature/Awoo/Control/Seguridad/Admin");

    onValue(
      celularRef,
      (snap) => {
        const numeroCompleto = snap.val(); // ejemplo: "573226144536"
        const telefonoSinPrefijo = numeroCompleto.toString().slice(2); // elimina el "57"

        if (telefonoInput !== telefonoSinPrefijo) {
          setTelefonoError("📵 Número incorrecto.");
          return;
        }

        // Si coincide, obtenemos la clave y enviamos por WhatsApp
        onValue(
          claveRef,
          (claveSnap) => {
            const clave = claveSnap.val();
            const mensaje = `Hola, tu clave de administrador es: ${clave}`;
            const url = `https://wa.me/${numeroCompleto}?text=${encodeURIComponent(mensaje)}`;
            window.open(url, "_blank"); // Abre WhatsApp Web
            setRecoveryOpen(false);
            setTelefonoError("");
            setTelefonoInput("");
          },
          { onlyOnce: true }
        );
      },
      { onlyOnce: true }
    );
  };

  const obtenerTodasLasClaves = () => {
    const clavesRef = ref(db, "nature/Awoo/Control/Seguridad/Claves");
    const adminRef = ref(db, "nature/Awoo/Control/Seguridad/Admin");

    onValue(clavesRef, (snapshot) => {
      const claves = snapshot.val() || {};
      onValue(adminRef, (adminSnap) => {
        const adminClave = adminSnap.val() || "";
        const dataCompleta = {
          Admin: adminClave,
          ...claves,
        };
        setClavesData(dataCompleta);
        setEditFields(dataCompleta);
        setEditDialogOpen(true);
        setCambios(false);
      }, { onlyOnce: true });
    }, { onlyOnce: true });
  };

  const handleFieldChange = (key, value) => {
    const nuevoEstado = {
      ...editFields,
      [key]: value,
    };
    setEditFields(nuevoEstado);

    const hayCambios = Object.keys(clavesData).some(
      (k) => clavesData[k] !== nuevoEstado[k]
    );
    setCambios(hayCambios);
  };

  const guardarCambios = () => {
    const nuevasClaves = { ...editFields };
    const nuevaAdmin = nuevasClaves.Admin;
    delete nuevasClaves.Admin;

    const updates = {
      "nature/Awoo/Control/Seguridad/Admin": nuevaAdmin,
      "nature/Awoo/Control/Seguridad/Claves": nuevasClaves,
    };

    update(ref(db), updates)
      .then(() => {
        setCambios(false);
        setClavesData(editFields);
        alert("✅ Claves actualizadas correctamente");
      })
      .catch((error) => {
        alert("❌ Error al guardar: " + error.message);
      });
  };

  return (
    <>
      {/* Diálogo de ingreso de clave */}
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>🔐 Acceso de Administrador</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Ingresa la clave para continuar:
          </Typography>
          <TextField
            type="password"
            label="Clave"
            fullWidth
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            error={!!error}
            helperText={error}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption">
            <Link href="#" underline="hover" onClick={() => setRecoveryOpen(true)}>
              ¿Olvidaste la clave? Recuperar clave
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={verificarClave} variant="contained" color="primary">
            Ingresar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de recuperación */}
      <Dialog open={recoveryOpen} onClose={() => setRecoveryOpen(false)}>
        <DialogTitle>📲 Verificación de Teléfono</DialogTitle>
        <DialogContent>
          <Typography>
            Ingresa tu número de celular registrado:
          </Typography>
          <TextField
            label="Celular"
            fullWidth
            value={telefonoInput}
            onChange={(e) => setTelefonoInput(e.target.value)}
            error={!!telefonoError}
            helperText={telefonoError}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecoveryOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={enviarRecuperacion}>
            Verificar y Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para editar claves */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>🔑 Editar Claves</DialogTitle>
        <DialogContent>
          {Object.entries(editFields).map(([key, value]) => (
            <Box key={key} sx={{ my: 1 }}>
              <TextField
                label={key}
                value={value}
                fullWidth
                onChange={(e) => handleFieldChange(key, e.target.value)}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cerrar</Button>
          <Button
            onClick={guardarCambios}
            variant="contained"
            color="success"
            disabled={!cambios}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminClaveDialog;
